import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import {
  CoSponsorCard,
  FullSponsorRow,
  SponsorSkeleton,
} from "components/Sponsors/SponsorLists";

/**
 * `years` / `onSelectYear` are optional. When more than one season is available the head grows a
 * row of tabs, so the running year and the archive are one dialog rather than two places to look.
 */
function SponsorsModal({ data, year, years, onSelectYear, loading, onClose }) {
  const closeRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    if (closeRef.current != null) {
      closeRef.current.focus();
    }
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const handleJump = useCallback((anchorId) => {
    const target = document.getElementById(anchorId);
    if (target == null) {
      return;
    }
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.classList.add("is-target");
    window.setTimeout(() => target.classList.remove("is-target"), 2200);
  }, []);

  return createPortal(
    <div
      className="sp-modal-scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="sp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sponsorsModalTitle"
      >
        <div className="sp-modal-head">
          <p className="sp-eyebrow">Durgotsav {year}</p>
          <h2 id="sponsorsModalTitle">Our Sponsors</h2>
          <p>Every offering below was made possible by a member of this community.</p>
          {Array.isArray(years) && years.length > 1 && onSelectYear != null && (
            <div className="sp-years" role="tablist" aria-label="Sponsorship year">
              {years.map((entry) => (
                <button
                  className={"sp-year" + (entry === year ? " is-on" : "")}
                  type="button"
                  role="tab"
                  aria-selected={entry === year}
                  key={entry}
                  onClick={() => onSelectYear(entry)}
                >
                  {entry}
                </button>
              ))}
            </div>
          )}
          <button
            className="sp-modal-close"
            type="button"
            onClick={onClose}
            aria-label="Close"
            ref={closeRef}
          >
            &times;
          </button>
        </div>

        <div className="sp-modal-body" ref={bodyRef}>
          {loading === true && <SponsorSkeleton rows={8} />}

          {loading !== true && data.fullSponsors.length === 0 && data.coSponsors.length === 0 && (
            <div className="sp-open-state">
              <span className="sp-open-dot" />
              <span>
                <b>No sponsorships yet this season</b> &mdash; every offering is still open.
              </span>
            </div>
          )}

          {loading !== true && data.fullSponsors.length > 0 && (
            <div>
              <div className="sp-modal-sec-title">
                Full Sponsorship <span>{data.fullSponsors.length} offerings</span>
              </div>
              <div className="sp-grid">
                {data.fullSponsors.map((entry, index) => (
                  <FullSponsorRow
                    entry={entry}
                    onJump={handleJump}
                    key={`${entry.offering}-${index}`}
                  />
                ))}
              </div>
            </div>
          )}

          {loading !== true && data.coSponsors.length > 0 && (
            <div>
              <div className="sp-modal-sec-title">
                Co-Sponsorship <span>{data.coSponsors.length} offerings</span>
              </div>
              <div className="sp-cards">
                {data.coSponsors.map((entry) => (
                  <CoSponsorCard entry={entry} key={entry.anchorId} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sp-modal-foot">
          <span>
            Showing Durgotsav {year}
            {data.live === true && " \u00b7 updated as contributions come in"}
          </span>
          <a href="#/sponsors">Open the full Sponsors page &rarr;</a>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SponsorsModal;
